import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPublishedEvents, getChildEvents } from '../lib/api/events';
import { getMyApplications } from '../lib/api/applications';
import type { Event } from '../types/database';
import { captureError } from '../lib/capture-error';
import { localDateToMs, parseLocalDate } from '../lib/dates';
import { getEventPhase, hasEventEnded, isPastEvent } from '../lib/event-status';
import EventPhaseBadge from '../components/EventPhaseBadge';
import { summarizeEventDescription } from '../lib/event-description';

const EventsPage: React.FC = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [childIdsByParentId, setChildIdsByParentId] = useState<Record<string, string[]>>({});
  const [appliedEventIds, setAppliedEventIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const publishedEvents = await getPublishedEvents();
        const now = new Date();
        const sortedEvents = publishedEvents
          .filter((event) => !isPastEvent(event, now))
          .sort(
            (a, b) =>
              Number(hasEventEnded(a, now)) - Number(hasEventEnded(b, now)) ||
              localDateToMs(a.start_date) - localDateToMs(b.start_date)
          );
        setEvents(sortedEvents);
        // Load child event ids for parents (for "You've applied" badge)
        const childIds: Record<string, string[]> = {};
        await Promise.all(
          sortedEvents
            .filter((e) => e.is_parent_event)
            .map(async (e) => {
              const children = await getChildEvents(e.id);
              childIds[e.id] = children.map((c) => c.id);
            })
        );
        setChildIdsByParentId(childIds);
      } catch (err) {
        captureError(err, 'Failed to load events');
        setError('Failed to load events');
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, []);

  useEffect(() => {
    if (!user) {
      setAppliedEventIds(new Set());
      return;
    }
    let cancelled = false;
    getMyApplications(user.id)
      .then((apps) => {
        if (cancelled) return;
        const ids = new Set((apps || []).map((a) => a.event?.id).filter(Boolean) as string[]);
        setAppliedEventIds(ids);
      })
      .catch(() => {
        if (!cancelled) setAppliedEventIds(new Set());
      });
    return () => { cancelled = true; };
  }, [user]);

  if (loading) {
    return (
      <div className="container my-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container my-5">
        <div className="alert alert-danger text-center">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <h1 className="text-center">Upcoming Events</h1>
      <p className="text-center">Explore our upcoming events and join us in making an impact!</p>
      {events.length === 0 && (
        <p className="text-center text-muted">
          No upcoming events right now. Check back soon, or see our{' '}
          <Link to="/past-events">past events</Link>.
        </p>
      )}
      <div className="row justify-content-center">
        {events.map((event) => {
          const isParent = event.is_parent_event && (childIdsByParentId[event.id]?.length ?? 0) > 0;
          const hasAppliedToThisOrChild =
            appliedEventIds.has(event.id) ||
            (childIdsByParentId[event.id]?.some((id) => appliedEventIds.has(id)) ?? false);

          return (
            <div className="col-md-4 mb-4" key={event.id}>
              <div className="card h-100">
                {event.featured_image_url && (
                  <img 
                    src={event.featured_image_url} 
                    alt={event.title} 
                    className="card-img-top" 
                    style={{ objectFit: 'contain', maxHeight: 200, width: '100%', background: '#f8f9fa' }} 
                  />
                )}
                <div className="card-body">
                  <EventPhaseBadge phase={getEventPhase(event)} className="mb-2" />
                  <h5 className="card-title d-flex align-items-center gap-2 flex-wrap">
                    {event.title}
                    {hasAppliedToThisOrChild && (
                      <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>You&apos;ve applied</span>
                    )}
                  </h5>
                  {event.description && (
                    <p className="card-text">{summarizeEventDescription(event.description)}</p>
                  )}
                  <p className="card-text text-muted">
                    {event.start_date &&
                      parseLocalDate(event.start_date).toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric',
                      })}
                  </p>
                  <div className="mt-3 d-flex gap-2">
                    <Link
                      to={`/events/${event.slug}`}
                      className="btn btn-primary btn-sm"
                    >
                      <i className={`fas ${isParent ? 'fa-map-marker-alt' : 'fa-info-circle'} me-1`} aria-hidden></i>
                      {isParent ? 'View sites & register' : 'View details'}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EventsPage;